/*
  MAV Hire — journey, funnel and form-abandonment tracking (v1, 9 Sept 2026)

  Four jobs, one file:

  1. Journey trail. Records the pages viewed this visit, in order, with how
     long on each, in sessionStorage. Attached to every enquiry as
     contact[Journey], so the lead arrives saying what they were looking at
     before they got in touch. Never leaves the visitor's own browser except
     as part of an enquiry they chose to send.

  2. Funnel stages. One event per stage per session, with distinct event
     names so GA4 Funnel exploration can use them with no extra setup:
       stage_land → stage_browse → stage_quote_view → stage_quote_start → stage_enquiry

  3. Quote form instrumentation. quote_form_view / _start / _submit, plus
     quote_form_abandon when someone starts typing and leaves without
     sending — carrying the last field they touched, which is the bit that
     tells us what to fix. As of Sept 2026 that gap was 21 starts to 7
     submissions in 30 days.

  4. Scroll depth. scroll_50 and scroll_75 on content pages. GA4's built-in
     "scroll" already covers 90%, so these fill in the curve below it.

  Event parameters (last_field, stage, percent…) show up in GA4 DebugView and
  realtime immediately, but need registering as custom dimensions in the GA4
  UI before they can be used in standard reports.
*/
(function () {
  var JOURNEY_KEY = 'mav_journey_v1';
  var STAGE_KEY   = 'mav_stage_v1';
  var MAX_PAGES   = 12;
  var MAX_CHARS   = 1500;

  function ss(fn, fallback) { try { return fn(); } catch (e) { return fallback; } }
  function now() { return Date.now(); }

  var isTouch = window.matchMedia('(hover: none), (pointer: coarse)').matches;

  /* ------------------------------------------------------------------
     Reporting
  ------------------------------------------------------------------ */
  // gtag.js may still be loading when an early event fires, and an event sent
  // before a destination is configured is never delivered to it. Queue the
  // command exactly as the standard gtag stub does, so the tag replays it in
  // order once it is ready.
  function sendGtag() {
    window.dataLayer = window.dataLayer || [];
    if (typeof window.gtag === 'function') window.gtag.apply(null, arguments);
    else window.dataLayer.push(arguments);
  }

  function fire(name, params) {
    params = params || {};
    window.dataLayer = window.dataLayer || [];
    var push = { event: name };
    for (var k in params) { if (params.hasOwnProperty(k)) push[k] = params[k]; }
    window.dataLayer.push(push);
    sendGtag('event', name, params);
  }

  /* ------------------------------------------------------------------
     1. Journey trail
  ------------------------------------------------------------------ */
  function readJourney() {
    return ss(function () { return JSON.parse(sessionStorage.getItem(JOURNEY_KEY) || '[]'); }, []) || [];
  }
  function writeJourney(arr) {
    ss(function () { sessionStorage.setItem(JOURNEY_KEY, JSON.stringify(arr)); });
  }

  function recordPage() {
    var arr = readJourney();
    var last = arr[arr.length - 1];

    // Close off the previous page's dwell time.
    if (last && last.d == null) last.d = Math.min(Math.round((now() - last.s) / 1000), 3600);

    // A refresh or a hash change isn't a new page.
    if (last && last.p === window.location.pathname) {
      writeJourney(arr);
      return;
    }

    arr.push({
      p: window.location.pathname,
      t: (document.title || '').replace(/\s*[|–—-]\s*MAV Hire.*$/i, '').trim().slice(0, 70),
      s: now(),
      d: null
    });
    if (arr.length > MAX_PAGES) arr = arr.slice(arr.length - MAX_PAGES);
    writeJourney(arr);
  }

  function closeCurrentPage() {
    var arr = readJourney();
    var last = arr[arr.length - 1];
    if (!last) return;
    if (last.p === window.location.pathname) {
      last.d = Math.min(Math.round((now() - last.s) / 1000), 3600);
      writeJourney(arr);
    }
  }

  function mmss(secs) {
    if (secs < 60) return secs + 's';
    var m = Math.floor(secs / 60), s = secs % 60;
    return m + 'm' + (s < 10 ? '0' : '') + s + 's';
  }

  function formatJourney() {
    var arr = readJourney();
    if (!arr.length) return '';
    var lines = [], total = 0;
    for (var i = 0; i < arr.length; i++) {
      var e = arr[i];
      var d = (e.d != null) ? e.d : Math.min(Math.round((now() - e.s) / 1000), 3600);
      total += d;
      lines.push((i + 1) + '. ' + (e.t || 'Untitled') + '  —  ' + e.p + '  (' + mmss(d) + ')');
    }
    var head = 'Viewed ' + arr.length + ' page' + (arr.length === 1 ? '' : 's') +
               ' over ' + mmss(total) + ' on ' + (isTouch ? 'mobile' : 'desktop') + ':';
    var out = head + '\n' + lines.join('\n');
    return out.length > MAX_CHARS ? out.slice(0, MAX_CHARS - 1) + '…' : out;
  }

  recordPage();
  window.addEventListener('pagehide', closeCurrentPage);
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') closeCurrentPage();
  });

  /* ------------------------------------------------------------------
     2. Funnel stages — once per session each
  ------------------------------------------------------------------ */
  function stage(name) {
    var done = ss(function () { return JSON.parse(sessionStorage.getItem(STAGE_KEY) || '{}'); }, {}) || {};
    if (done[name]) return;
    done[name] = 1;
    ss(function () { sessionStorage.setItem(STAGE_KEY, JSON.stringify(done)); });
    fire('stage_' + name, { page_path: window.location.pathname });
  }

  var path = window.location.pathname;

  // Hold the entry stages until the page has finished loading. Shopify's own
  // Google tag configures GA4 asynchronously, and anything sent before that
  // config lands reaches Ads but not GA4 — which is precisely what happened to
  // stage_land on the first live test (9 Sept 2026).
  function markEntryStages() {
    stage('land');
    if (path.indexOf('/products/') === 0 || path.indexOf('/collections/') === 0) stage('browse');
  }
  if (document.readyState === 'complete') setTimeout(markEntryStages, 800);
  else window.addEventListener('load', function () { setTimeout(markEntryStages, 800); });

  /* ------------------------------------------------------------------
     3. Quote forms — view, start, abandon, submit
  ------------------------------------------------------------------ */
  function isQuoteForm(form) {
    var action = form.getAttribute('action') || '';
    return action.indexOf('/contact') > -1;
  }

  function fieldName(el) {
    var n = el.getAttribute('name') || el.id || '';
    var m = n.match(/^contact\[(.+)\]$/);
    if (m) n = m[1];
    return n.replace(/^mav-exit-/, '').replace(/^c-/, '') || 'unknown';
  }

  function filledCount(form) {
    var n = 0;
    var els = form.querySelectorAll('input, textarea, select');
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (el.type === 'hidden' || el.tabIndex === -1) continue;
      if (el.value && el.value.trim()) n++;
    }
    return n;
  }

  function watchForm(form) {
    if (form.__mavWatched) return;
    form.__mavWatched = true;

    var state = { viewed: false, started: false, submitted: false, lastField: '' };
    var where = form.closest('#mav-exit') ? 'exit popup' : 'page';

    function viewed() {
      if (state.viewed) return;
      state.viewed = true;
      fire('quote_form_view', { form_location: where, page_path: window.location.pathname });
      stage('quote_view');
    }

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting) { viewed(); io.disconnect(); }
        }
      }, { threshold: 0.35 });
      io.observe(form);
    } else {
      viewed();
    }

    form.addEventListener('focusin', function (e) {
      if (!e.target.name && !e.target.id) return;
      if (e.target.type === 'hidden') return;
      state.lastField = fieldName(e.target);
      if (state.started) return;
      state.started = true;
      viewed();
      fire('quote_form_start', { form_location: where, page_path: window.location.pathname });
      stage('quote_start');
    });

    form.addEventListener('input', function (e) {
      if (e.target.type !== 'hidden') state.lastField = fieldName(e.target);
    });

    form.addEventListener('submit', function () {
      state.submitted = true;
      fire('quote_form_submit', { form_location: where, page_path: window.location.pathname });
      stage('enquiry');
    });

    function maybeAbandon() {
      if (!state.started || state.submitted || state.abandoned) return;
      state.abandoned = true;
      fire('quote_form_abandon', {
        form_location: where,
        last_field: state.lastField || 'unknown',
        fields_filled: filledCount(form),
        page_path: window.location.pathname
      });
    }
    window.addEventListener('pagehide', maybeAbandon);
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'hidden') maybeAbandon();
    });
  }

  function scanForms() {
    var forms = document.querySelectorAll('form[action*="/contact"]');
    for (var i = 0; i < forms.length; i++) watchForm(forms[i]);
  }
  scanForms();
  // The exit popup is in the DOM from the start, but re-scan in case a form
  // is rendered later by a section or an app.
  setTimeout(scanForms, 2000);

  /* ------------------------------------------------------------------
     Attach the journey to any enquiry as it is sent
  ------------------------------------------------------------------ */
  function setHidden(form, name, value) {
    var el = form.querySelector('input[name="' + name + '"]');
    if (!el) {
      el = document.createElement('input');
      el.type = 'hidden';
      el.name = name;
      form.appendChild(el);
    }
    el.value = value;
  }

  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || form.tagName !== 'FORM' || !isQuoteForm(form)) return;
    closeCurrentPage();
    var trail = formatJourney();
    if (trail) setHidden(form, 'contact[Journey]', trail);
  }, true);

  /* ------------------------------------------------------------------
     A call or a WhatsApp message is an enquiry too
  ------------------------------------------------------------------ */
  document.addEventListener('click', function (e) {
    var link = e.target && e.target.closest ? e.target.closest('a[href]') : null;
    if (!link) return;
    var h = (link.getAttribute('href') || '').toLowerCase();
    if (h.indexOf('tel:') === 0 || h.indexOf('wa.me') > -1 || h.indexOf('whatsapp.com') > -1) stage('enquiry');
  }, true);

  /* ------------------------------------------------------------------
     4. Scroll depth on content pages
  ------------------------------------------------------------------ */
  if (!/^\/(cart|checkout|account|apps|tools)(\/|$)/.test(path)) {
    var hit = {};
    var ticking = false;
    function checkDepth() {
      ticking = false;
      var doc = document.documentElement;
      var height = Math.max(doc.scrollHeight, document.body ? document.body.scrollHeight : 0);
      if (height <= window.innerHeight + 40) return;   // nothing to scroll
      var pct = ((window.pageYOffset + window.innerHeight) / height) * 100;
      [50, 75].forEach(function (mark) {
        if (pct >= mark && !hit[mark]) {
          hit[mark] = true;
          fire('scroll_' + mark, { page_path: window.location.pathname, percent: mark });
        }
      });
    }
    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(checkDepth);
    }, { passive: true });
  }
})();
